<template>
    <div class="provider-listing">
        <div v-for="provider in providers" :key="provider.id" class="provider flex">
            <div class="image">
                <a 
                    :href="providerUrl(provider.id, provider.name_for_url)" 
                    :target="isQueensOrg ? '' : '_blank'"
                >
                    <img :src="provider.avatar"/>
                </a>
            </div>
            <div class="details">
                <h2>
                    <a 
                        :href="providerUrl(provider.id, provider.name_for_url)"
                        :target="isQueensOrg ? '' : '_blank'"
                    >
                        {{ provider.name }}
                    </a>
                </h2>
                <div class="flex">
                    <div class="left-details">
                        <strong>{{ provider.position }}</strong>
                        <div class="services">
                            <span class="service primary" v-if="provider.servicesWithCredentialed"><i>{{ provider.servicesWithCredentialed }}</i></span>
                            <span class="service" v-if="provider.servicesWithoutCredentialedAndNotInCredentialed">(<i>{{ provider.servicesWithoutCredentialedAndNotInCredentialed }}</i>)</span>
                        </div>

                        <div class="provider-logos">
                            <div v-if="provider.tiers.includes('qmg')" class="logo">
                                <div class="logo-wrapper">
                                    <img class="qmg-logo" src="/wp-content/uploads/qhs-dir-api/qmg-logo.jpg">
                                    <div class="tooltip">
                                        <i class="far fa-question-circle"></i>
                                        <div class="tooltip-text">The Queen's University Medical Group (QUMG) is QHS' academic multi-specialty medical group.  QUMG is the preeminent academic medical group of the Pacific - leading the delivery of high quality, compassionate patient care and continually advancing education and research.  We provide quality patient care services throughout the State of Hawaii.  This includes urgent and ambulatory care services at clinics throughout the state and acute care services at The Queen's Medical Center's Punchbowl and West campuses.</div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="provider.tiers.includes('qcipn')" class="logo">
                                <div class="logo-wrapper">
                                    <img class="qcipn-logo" src="/wp-content/uploads/qhs-dir-api/qcipn-logo.jpg">
                                    <div class="tooltip">
                                        <i class="far fa-question-circle"></i>
                                        <div class="tooltip-text">The Queen's Clinically Integrated Physician Network is a collaboration between The Queen's Health System, community physicians, and Physician Organizations with a shared goal of providing high quality care, increased efficiency, and optimal patient health.</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <provider-features v-if="provider.tiers.includes('qcipn') || provider.tiers.includes('qmg') || provider.tiers.includes('enhanced_profile')" class="above-buttons" :provider="provider" />

                        <div class="flex">
                            <a 
                                :href="providerUrl(provider.id, provider.name_for_url)" class="btn-outlined"
                                :target="isQueensOrg ? '' : '_blank'"
                            >
                                View Profile
                            </a>
                        </div>
                    </div>
                    <div class="right-details">
                        <provider-features v-if="provider.tiers.includes('qcipn') || provider.tiers.includes('qmg') || provider.tiers.includes('enhanced_profile')"  class="below-buttons" :provider="provider" />
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!providers.length && !loading">No providers matched your search.</div>
        <div v-if="loading" class="provider flex preloading">
            <div class="image">
                <a href="#">
                    <img src="/wp-content/uploads/qhs-dir-api/provider-default.jpg"/>
                </a>
            </div>
            <div class="details">
                <div class="loader">
                    <div class="qhs-loader">
                        <div class="pulse"></div>
                    </div>
                </div>
                <div class="flex">
                    <div class="left-details">
                        <div class="services">
                            <span class="service primary"></span>
                            <span class="service"></span>
                        </div>

                        <div class="flex">
                            <a class="btn-outlined">

                            </a>
                            <a class="btn-outlined longer">

                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <a href="#scrollTop" class="btn-scroll-top">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" role="presentation" class="vs__open-indicator"><path d="M9.211364 7.59931l4.48338-4.867229c.407008-.441854.407008-1.158247 0-1.60046l-.73712-.80023c-.407008-.441854-1.066904-.441854-1.474243 0L7 5.198617 2.51662.33139c-.407008-.441853-1.066904-.441853-1.474243 0l-.737121.80023c-.407008.441854-.407008 1.158248 0 1.600461l4.48338 4.867228L7 10l2.211364-2.40069z"></path></svg>
        </a>

        <div ref="paginationChecker"></div>
    </div>
</template>

<script>
import ProviderFeatures from './ProviderFeatures.vue';
import {mapState} from 'vuex';

export default {
    components: {ProviderFeatures},

    props: {
        query: {type: Object, default: null},
    },

    computed: {
        ...mapState(['providers', 'filterSelections', 'loading', 'nextPageUrl']),

        queryString() {
            if (this.query) {
                return this.query;
            }

            const query = this.filterSelections;
            return Object.keys(query)
                .map(key => key + '=' + (query[key] || ''))
                .join('&');
        },

        isQueensOrg() {
            return window.location.href.includes('queens.org');
        },
    },

    mounted() {
        window.addEventListener('scroll', () => {
            if (this.shouldLoadNextPage()) {
                this.$store.dispatch('getNextPageOfProviders', this.query || this.$route.query);
            }
        });
    },

    methods: {
        withLineBreaks(value) {
            return value.join('<br />');
        },

        providerUrl(providerId, providerName) {
            return `/providers/${providerId}/${providerName}/` + (this.queryString && Object.getPrototypeOf(this.queryString) != Object.prototype ? `?${this.queryString}` : '');
        },

        shouldLoadNextPage() {
            if (this.loading || !this.nextPageUrl) {
                return;
            }

            const rect = this.$refs.paginationChecker.getBoundingClientRect();

            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        },
    },
};
</script>
